import { themeDefaults } from '@youscience/theme';
import { CoreBox, CoreTypography } from '@youscience/core';
import { ScholarshipDetail } from '@common-types';

import { Container } from '@components/Layout/components/Container';
import { Chart } from '@components/Chart';

import { moneyFormatter, separateDigitsByComma } from '@utils/formatter';

import {
  sxStyles,
  StyledSectionsWrap,
  StyledChartSectionInner,
  StyledInfoSectionInner,
  StyledInfoItem,
} from './StatisticsSection.styles';

const GREY_COLOR = themeDefaults?.palette?.text?.disabled;

const renderInfoItem = (title: string, subtitle: string, value: number, color = GREY_COLOR) => {
  if (!value) return;

  return (
    <StyledInfoItem>
      <CoreBox>
        <CoreTypography sx={sxStyles.infoItemTitle} variant="body2">
          {title}
        </CoreTypography>

        <CoreTypography variant="caption">{subtitle}</CoreTypography>
      </CoreBox>

      <CoreTypography sx={{ color }} variant="h4" component="h3">
        {separateDigitsByComma.format(value)}
      </CoreTypography>
    </StyledInfoItem>
  );
};

export interface ThemeOptions {
  chartsColorPalette: { indigo: { main: string }; teal: { main: string } };
}

interface StatisticsSectionProps {
  scholarship: ScholarshipDetail;
}

export const StatisticsSection = (props: StatisticsSectionProps) => {
  const { awardTotals, awardRange, moneyRange } = props.scholarship;

  const availableAwardsValue = awardRange?.upperLimit
    ? `${awardRange?.lowerLimit}-${awardRange?.upperLimit}`
    : awardRange?.lowerLimit;
  const moneyRangeValue = moneyRange?.upperLimit || moneyRange?.lowerLimit;

  const hasInfoData =
    awardTotals?.totalApplicants || (awardTotals?.totalAmount && awardTotals?.totalNumber) || awardRange?.lowerLimit;

  if (!hasInfoData && !moneyRange?.lowerLimit) return null;

  return (
    <Container>
      <CoreTypography sx={sxStyles.title} variant="h3" component="h2">
        Statistics
      </CoreTypography>

      <StyledSectionsWrap>
        {moneyRangeValue ? (
          <StyledChartSectionInner>
            <CoreTypography>Award up to</CoreTypography>

            <CoreTypography sx={sxStyles.awardUpToValue} variant="h4" component="h3">
              {moneyFormatter.format(moneyRangeValue)}
            </CoreTypography>

            <CoreTypography variant="caption">
              The dollar value of each award per individual applicant within the program.
            </CoreTypography>

            <Chart value={moneyRangeValue} min={25} max={300000} />
          </StyledChartSectionInner>
        ) : null}

        {hasInfoData ? (
          <StyledInfoSectionInner>
            <CoreTypography variant="h4" component="h3">
              Award Totals
            </CoreTypography>

            <CoreTypography variant="caption">
              The program’s total number of awards distributed and total dollar value.
            </CoreTypography>

            <CoreBox sx={sxStyles.infoItemsWrap}>
              {renderInfoItem(
                'Awards granted',
                'The programs total number of awards',
                awardTotals?.totalNumber ?? 0,
                (themeDefaults as ThemeOptions).chartsColorPalette.indigo.main,
              )}

              {renderInfoItem(
                'Money',
                'Total dollar value',
                awardTotals?.totalAmount ?? 0,
                (themeDefaults as ThemeOptions).chartsColorPalette.teal.main,
              )}

              {renderInfoItem('Applicants', 'The total number of applicants', awardTotals?.totalApplicants ?? 0)}

              {awardRange?.lowerLimit ? (
                <StyledInfoItem>
                  <CoreBox>
                    <CoreTypography sx={sxStyles.infoItemTitle} variant="body2">
                      Available awards
                    </CoreTypography>

                    <CoreTypography variant="caption">
                      The total number of available awards within the program for current year
                    </CoreTypography>
                  </CoreBox>

                  <CoreTypography sx={sxStyles.availableAwardsValue} variant="h4" component="h3">
                    {availableAwardsValue}
                  </CoreTypography>
                </StyledInfoItem>
              ) : null}
            </CoreBox>
          </StyledInfoSectionInner>
        ) : null}
      </StyledSectionsWrap>
    </Container>
  );
};
